import { useQuery } from '@apollo/client/react';
import { Descriptions, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Spacer } from '../../components/Spacer';
import { ADMIN_ORDER } from '../../gqls/order';
import { executorCategories } from '../../utils';

export const PageOrder = () => {
  const params = useParams();

  const { data, loading } = useQuery(ADMIN_ORDER, { variables: { where: { id: params.id } } });

  const adminOrder = data?.adminOrder;

  return (
    <>
      <Typography.Title level={5} style={{ margin: 0 }}>
        Заказ{adminOrder ? ` № ${adminOrder.number}` : ''}
      </Typography.Title>
      <Spacer h={12} />
      {loading ? (
        <Spin />
      ) : adminOrder ? (
        <>
          <Descriptions
            layout="vertical"
            bordered
            items={[
              {
                key: 'createdAt',
                label: 'Дата создания',
                children: dayjs(adminOrder.createdAt).format('DD.MM.YYYY HH:mm'),
              },
              {
                key: 'user',
                label: 'Заказчик',
                children: (
                  <Link to={`/users/${adminOrder.user.id}`}>
                    {adminOrder.user.name} ({adminOrder.user.phone})
                  </Link>
                ),
              },
              {
                key: 'executor',
                label: 'Исполнитель',
                children: adminOrder.executor ? (
                  <Link to={`/executors/${adminOrder.executor.id}`}>
                    {adminOrder.executor.fullName} ({adminOrder.executor.user.phone})
                  </Link>
                ) : (
                  'Не найден'
                ),
              },
              {
                key: 'status',
                label: 'Статус',
                children: getStatus(adminOrder.status),
              },
              {
                key: 'category',
                label: 'Категория',
                children: (
                  <Tag>
                    {executorCategories.find((item) => item.value === adminOrder.category)?.singleLabel || ''}
                    {adminOrder.category === 'heavy-truck'
                      ? ` ${adminOrder.heavyTruckType === 'trawl' ? '(Трал)' : '(Полуприцеп)'}`
                      : ''}
                  </Tag>
                ),
              },
              {
                key: 'fullAddress',
                label: 'Адрес',
                children: adminOrder.fullAddress,
              },
              {
                key: 'datetime',
                label: 'Дата и время выполнения',
                children: dayjs(adminOrder.datetime).format('DD.MM.YYYY HH:mm'),
              },
              {
                key: 'loadersCount',
                label: 'Количество грузчиков',
                children: adminOrder.loadersCount,
              },
              {
                key: 'price',
                label: 'Цена',
                children: adminOrder.price ? `${adminOrder.price} ₽` : 'Не указана',
                span: 'filled',
              },
              {
                key: 'description',
                label: 'Описание',
                children: adminOrder.description,
                span: 'filled',
              },
              {
                key: 'photos',
                label: 'Фотографии',
                children: adminOrder.photos ? (
                  <>
                    {adminOrder.photos.map((photo) => (
                      <a key={photo} href={photo} target="_blank" rel="noreferrer">
                        <img src={photo} alt="orderPhoto" style={{ maxWidth: 120, marginRight: 8 }} />
                      </a>
                    ))}
                  </>
                ) : (
                  'Нет фото'
                ),
                span: 'filled',
              },
            ]}
          />
          <Spacer h={24} />
          <Typography.Title level={5} style={{ margin: 0 }}>
            Предложения исполнителей
          </Typography.Title>
          <Spacer h={12} />
          <ContentTable>
            <Table
              size="middle"
              loading={loading}
              dataSource={adminOrder.offers}
              columns={[
                {
                  key: 'createdAt',
                  dataIndex: 'createdAt',
                  title: 'Дата предложения',
                  render: (createdAt) => dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
                },
                {
                  key: 'executor',
                  dataIndex: 'executor',
                  title: 'Исполнитель',
                  render: (_, offer) => <Link to={`/executors/${offer.executor.id}`}>{offer.executor.fullName}</Link>,
                },
                {
                  key: 'price',
                  dataIndex: 'price',
                  title: 'Цена',
                  render: (price) => (price ? `${price} ₽` : 'Не указана'),
                },
                {
                  key: 'status',
                  dataIndex: 'status',
                  title: 'Статус',
                  render: (status) => {
                    // new, selected, cancelled
                    if (status === 'new') return <Tag color="blue">Новый</Tag>;
                    if (status === 'selected') return <Tag color="green">Выбран</Tag>;
                    if (status === 'cancelled') return <Tag color="red">Отменен</Tag>;
                    return <Tag>{status}</Tag>;
                  },
                },
              ]}
            />
          </ContentTable>
        </>
      ) : null}
    </>
  );
};

const getStatus = (status) => {
  // searching, active, done, cancelled, expired
  if (status === 'searching') return <Tag color="blue">Поиск исполнителя</Tag>;
  if (status === 'active') return <Tag color="green">Активный</Tag>;
  if (status === 'done') return <Tag color="gray">Выполнен</Tag>;
  if (status === 'cancelled') return <Tag color="red">Отменен</Tag>;
  if (status === 'expired') return <Tag color="orange">Истек срок</Tag>;
  return <Tag>{status}</Tag>;
};

const ContentTable = styled.div`
  overflow-x: auto;

  table {
    min-width: 1100px;
  }
`;
